<template>
  <v-card>
    <v-card-title>
      <span class="headline">Package Usage Analytics</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="refreshAnalytics" :loading="refreshing">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon @click="toggleExpanded">
        <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
    </v-card-title>

    <v-expand-transition>
      <v-card-text v-show="expanded">
        <v-container>
          <v-row>
          <!-- Summary Stats -->
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 font-weight-bold primary--text">
                  {{ analytics.summary?.totalUsage || 0 }}
                </div>
                <div class="text-caption">Total Scans</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 font-weight-bold info--text">
                  {{ analytics.summary?.uniquePackageTypes || 0 }}
                </div>
                <div class="text-caption">Package Types Used</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 font-weight-bold success--text">
                  {{ analytics.summary?.averageUsagePerDay || 0 }}
                </div>
                <div class="text-caption">Avg. Daily Usage</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card outlined v-if="analytics.summary?.mostUsedPackage">
              <v-card-text class="text-center">
                <div class="text-subtitle-1 font-weight-bold">
                  {{ analytics.summary.mostUsedPackage.name }}
                </div>
                <div class="text-caption">Most Used Package</div>
                <v-chip x-small outlined class="mt-1">
                  {{ analytics.summary.mostUsedPackage.percentage }}%
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Top Packages Chart -->
          <v-col cols="12" md="8">
            <v-card outlined>
              <v-card-title class="subtitle-1">
                Top Package Types
              </v-card-title>
              <v-card-text>
                <v-simple-table dense v-if="analytics.packageRanking?.length">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Package Name</th>
                      <th>Usage Count</th>
                      <th>Percentage</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in topPackages" :key="item.packageTypeId">
                      <td>
                        <v-chip x-small :color="getRankColor(item.rank)">
                          #{{ item.rank }}
                        </v-chip>
                      </td>
                      <td class="font-weight-medium">{{ item.packageName }}</td>
                      <td>{{ item.usageCount }}</td>
                      <td>
                        <v-progress-linear
                          :value="item.percentage"
                          height="20"
                          :color="getRankColor(item.rank)"
                        >
                          {{ item.percentage }}%
                        </v-progress-linear>
                      </td>
                      <td>
                        <v-icon 
                          small 
                          :color="getTrendColor(item.trend)"
                        >
                          {{ getTrendIcon(item.trend) }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div v-else class="text-center py-8 grey--text">
                  No usage data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Context Breakdown -->
          <v-col cols="12" md="4">
            <v-card outlined>
              <v-card-title class="subtitle-1">
                Usage Context
              </v-card-title>
              <v-card-text>
                <div v-if="contextData.length" class="context-chart">
                  <v-list dense>
                    <v-list-item v-for="(item, index) in contextData" :key="index">
                      <v-list-item-icon>
                        <v-icon small :color="getContextColor(item.context)">
                          {{ getContextIcon(item.context) }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ formatContext(item.context) }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip small outlined>
                          {{ item.count }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
                <div v-else class="text-center py-8 grey--text">
                  No context data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-expand-transition>
  </v-card>
</template>

<script>
import { defineComponent } from 'vue'
import { PackageTypeService } from '@/services/PackageTypeService'

export default defineComponent({
  name: 'PackageAnalytics',
  props: {
    analytics: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      packageService: null,
      expanded: this.getStoredExpandedState(),
      refreshing: false
    }
  },
  computed: {
    topPackages() {
      return this.analytics.packageRanking?.slice(0, 5) || []
    },
    contextData() {
      const breakdown = this.analytics.contextBreakdown || {}
      return Object.entries(breakdown).map(([context, count]) => ({
        context,
        count
      })).sort((a, b) => b.count - a.count)
    }
  },
  created() {
    this.packageService = new PackageTypeService()
  },
  methods: {
    async refreshAnalytics() {
      this.refreshing = true
      try {
        const data = await this.packageService.getAnalytics()
        this.$emit('refresh', data)
        this.$emit('show-snackbar', {
          text: 'Analytics refreshed',
          color: 'success'
        })
      } catch (error) {
        console.error('Failed to refresh analytics:', error)
        this.$emit('show-snackbar', {
          text: 'Failed to refresh analytics',
          color: 'error'
        })
      } finally {
        this.refreshing = false
      }
    },
    getStoredExpandedState() {
      try {
        const stored = localStorage.getItem('packageAnalyticsExpanded')
        return stored ? JSON.parse(stored) : true // Default to expanded
      } catch (error) {
        console.warn('Failed to load analytics expanded state from localStorage:', error)
        return true // Default to expanded on error
      }
    },
    toggleExpanded() {
      this.expanded = !this.expanded
      // Save state to localStorage
      localStorage.setItem('packageAnalyticsExpanded', JSON.stringify(this.expanded))
    },
    getRankColor(rank) {
      switch (rank) {
        case 1: return 'gold'
        case 2: return 'grey'
        case 3: return 'brown'
        default: return 'primary'
      }
    },
    getTrendIcon(trend) {
      switch (trend) {
        case 'up': return 'mdi-trending-up'
        case 'down': return 'mdi-trending-down'
        default: return 'mdi-trending-neutral'
      }
    },
    getTrendColor(trend) {
      switch (trend) {
        case 'up': return 'success'
        case 'down': return 'error'
        default: return 'grey'
      }
    },
    getContextIcon(context) {
      switch (context.toLowerCase()) {
        case 'manual': return 'mdi-hand'
        case 'scan': return 'mdi-qrcode-scan'
        case 'api': return 'mdi-api'
        case 'auto': return 'mdi-robot'
        default: return 'mdi-dots-horizontal'
      }
    },
    getContextColor(context) {
      switch (context.toLowerCase()) {
        case 'manual': return 'primary'
        case 'scan': return 'success'
        case 'api': return 'info'
        case 'auto': return 'warning'
        default: return 'grey'
      }
    },
    formatContext(context) {
      return context.charAt(0).toUpperCase() + context.slice(1).toLowerCase()
    }
  }
})
</script>

<style scoped>
.context-chart {
  max-height: 300px;
  overflow-y: auto;
}
</style>